import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { AuthService } from '../../../core/auth/auth.service';
import { AiConsentCheckboxComponent } from '../../../shared/ai-consent/ai-consent-checkbox/ai-consent-checkbox';
import { LegalModalComponent } from '../../../shared/legal-modal/legal-modal';
import { GENERIC_ERROR } from '../../../core/http/read-error';

@Component({
  selector: 'app-register',
  imports: [
    RouterLink,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    AiConsentCheckboxComponent,
  ],
  templateUrl: './register.html',
  styleUrl: './register.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly emailAlreadyRegistered = signal(false);

  protected readonly form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    termsAccepted: [false, Validators.requiredTrue],
    // POPIA s72 — the consent to AI-assisted assessment, asked ONCE here as
    // its own question (never folded into the terms) and standing for every
    // application this account makes. NOT required: Constitution §A6.7's
    // ground is that an agreement you cannot open an account without giving
    // is not an agreement. Left unticked, the account is still created and the
    // application screen puts the question again before anything is sent.
    aiConsent: [false],
  });

  protected onAiConsentChange(checked: boolean): void {
    this.form.controls.aiConsent.setValue(checked);
  }

  protected get emailControl() {
    return this.form.controls.email;
  }

  protected openLegal(type: 'terms' | 'privacy'): void {
    this.dialog.open(LegalModalComponent, { data: { type }, maxWidth: '720px', width: '90vw' });
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.isLoading() || this.form.invalid) return;

    this.errorMessage.set(null);
    this.emailAlreadyRegistered.set(false);
    this.isLoading.set(true);

    const { email, termsAccepted, aiConsent } = this.form.getRawValue();

    this.authService
      .register({
        email: email!,
        terms_accepted: termsAccepted!,
        ai_processing_consent: aiConsent!,
      })
      .subscribe({
        next: () => {
          this.isLoading.set(false);
          // Email is passed via in-memory navigation state only — never as a URL query
          // param. URLs leak to access logs, browser history, Referer headers, and analytics.
          void this.router.navigate(['/auth/check-email'], {
            state: { email: email! },
          });
        },
        error: (err: unknown) => {
          this.isLoading.set(false);
          if (err instanceof HttpErrorResponse) {
            const authErr = this.authService.getAuthError(err);
            if (authErr?.code === 'VALIDATION_ERROR' && err.error?.detail) {
              const detail = err.error.detail as Record<string, string | string[]>;
              if (detail['email']) {
                this.emailAlreadyRegistered.set(true);
                const msg = Array.isArray(detail['email']) ? detail['email'][0] : detail['email'];
                this.emailControl.setErrors({ serverError: msg });
              }
            } else {
              this.errorMessage.set(authErr?.detail ?? 'Registration failed. Please try again.');
            }
          } else {
            this.errorMessage.set(GENERIC_ERROR);
          }
          this.cdr.markForCheck();
        },
      });
  }
}
